// Integración con MercadoLibre — capa de API de la pantalla.
//
// Dos superficies que cuentan la misma historia desde puntas opuestas del mismo dato: el
// vínculo producto↔publicación vive SOLO en la tabla `external_item` y se carga a mano en la
// ficha del producto.
//   - "Sin sincronizar": productos del catálogo sin ninguna publicación vinculada (lo que
//     todavía no se puso a la venta en ML, o se cargó mal).
//   - "Órdenes caídas": ventas de ML que no se pudieron ingresar, casi siempre porque la
//     publicación de esa venta no está vinculada. O sea: la consecuencia de lo anterior.
//
// Sigue el molde de `collectionsApi.js`: thin wrappers sobre fetch, en un solo archivo.

import { endSession } from './session';

const config = process.env;

const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;

const authHeaders = () => ({
    'access-token': localStorage.getItem('token'),
    'Content-Type': 'application/json',
});

// El 1002 sale por `endSession` y NO por `localStorage.getItem('homePageURL')`, que es lo que
// hacía la pantalla vieja: esa key sólo se escribe en el login, así que cuando falta el browser
// termina navegando a "/null" (misma trampa documentada en collectionsApi.js).
async function request(path, options = {}) {
    let res;
    try {
        res = await fetch(apiBase() + path, {
            ...options,
            headers: { ...authHeaders(), ...(options.headers || {}) },
        });
    } catch (networkError) {
        const err = new Error('No pudimos hablar con el servidor. Revisá la conexión y reintentá.');
        err.code = 0;
        throw err;
    }

    let body = null;
    try {
        body = await res.json();
    } catch (parseError) {
        body = null;
    }

    const code = body && Number(body.error) > 0 ? Number(body.error) : 0;

    if (code === 1002) {
        endSession();
        const err = new Error((body && body.message) || 'La sesión expiró');
        err.code = 1002;
        throw err;
    }

    if (!res.ok || code) {
        const err = new Error((body && body.message) || 'Ocurrió un error procesando la solicitud');
        err.code = code;
        err.body = body;
        throw err;
    }

    return body ? body.data : null;
}

// ── Auditoría de envío gratis regalado ───────────────────────────────────────────────────

// La última foto guardada del barrido (no consulta ML: instantáneo).
export function getFreeShippingAudit() {
    return request('configuration/settings/mercadolibre/free-shipping-audit');
}

// Corre el barrido. `dryRun` va en la QUERY además del body a propósito: el backend lo lee
// primero de `req.query` y su default es "corregir de verdad" (fail-open). Si el body se
// perdiera —un proxy, un Content-Type equivocado, un refactor del fetch— este mismo llamado
// dispararía una corrección masiva de stock en ML. La query sobrevive a todo eso.
export function runFreeShippingAudit() {
    return request('configuration/settings/mercadolibre/reconcile-stock?dryRun=1', {
        method: 'POST',
        body: JSON.stringify({ dryRun: true }),
    });
}

// Desestimar una publicación revisada / volver a vigilarla. Existe porque hay publicaciones
// que ML NO deja corregir (precio sobre el umbral: el envío gratis no se puede desactivar);
// sin esto quedan en rojo para siempre. Reaparecen solas si las condiciones empeoran.
export function dismissFreeShipping(externalItemId, note = '') {
    return request(`configuration/settings/mercadolibre/free-shipping-audit/${encodeURIComponent(externalItemId)}/dismiss`, {
        method: 'POST',
        body: JSON.stringify({ note }),
    });
}

export function undismissFreeShipping(externalItemId) {
    return request(`configuration/settings/mercadolibre/free-shipping-audit/${encodeURIComponent(externalItemId)}/dismiss`, {
        method: 'DELETE',
    });
}

// ── Órdenes caídas (dead-letter, ENI-64) ─────────────────────────────────────────────────

export const FAILED_ORDER_STATUS = {
    pending: { cls: 'badge-warning', text: 'Pendiente' },
    recovered: { cls: 'badge-success', text: 'Recuperada' },
    ignored: { cls: 'badge-secondary', text: 'Ignorada' },
};

export const FAILED_ORDER_REASON = {
    UNMAPPED_ITEM: 'Ítem sin vincular',
    NO_PRODUCTS: 'Sin productos',
    ERROR: 'Error',
};

export function getFailedOrders(status) {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return request(`ml/failedOrders${query}`).then((rows) => rows || []);
}

export function reprocessFailedOrder(id) {
    return request('ml/failedOrders/reprocess', { method: 'POST', body: JSON.stringify({ id }) });
}

export function ignoreFailedOrder(id) {
    return request('ml/failedOrders/ignore', { method: 'POST', body: JSON.stringify({ id }) });
}

// ── Catálogo sin publicación vinculada ───────────────────────────────────────────────────

// Devuelve { items, totals }. `totals` NO depende del texto buscado: sirve para rotular los
// filtros con cuántos hay de cada cosa aunque la búsqueda esté achicando la grilla.
export function getUnsyncedProducts({ withStock = false, includeHidden = false, search = '' } = {}) {
    const params = new URLSearchParams();
    if (withStock) params.set('withStock', '1');
    if (includeHidden) params.set('includeHidden', '1');
    if (search) params.set('search', search);
    const query = params.toString();
    return request(`ml/unsyncedProducts${query ? `?${query}` : ''}`)
        .then((data) => ({
            items: (data && data.items) || [],
            totals: (data && data.totals) || { total: 0, visible: 0, visibleWithStock: 0, hidden: 0, hiddenWithStock: 0 },
        }));
}

// ── Precios y margen ─────────────────────────────────────────────────────────────────────

// Cada publicación de ML contra nuestro precio de lista, con el margen ya descontado. Lee la
// foto que dejó "Revisar publicaciones": no habla con ML, es instantáneo. Si nunca se corrió
// el barrido no hay foto y `neverChecked` viene en true — que es distinto de "no hay
// publicaciones", y la pantalla lo tiene que decir distinto.
export function getMlPriceCheck({ search = '', sortBy = 'margin', page = 1, limit = 50, onlyLosses = false, includeInactive = false } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (sortBy) params.set('sortBy', sortBy);
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(limit));
    if (onlyLosses) params.set('onlyLosses', '1');
    if (includeInactive) params.set('includeInactive', '1');
    return request(`ml/priceCheck?${params.toString()}`)
        .then((data) => ({
            items: (data && data.items) || [],
            pagination: (data && data.pagination) || { page: 1, limit, total: 0, pages: 1 },
            totals: (data && data.totals) || { listings: 0, withMargin: 0, losing: 0, withoutCost: 0, cheaperInMl: 0, inactive: 0 },
            assumptions: (data && data.assumptions) || null,
            checkedAt: data ? data.checkedAt : null,
            neverChecked: data ? !!data.neverChecked : true,
        }));
}

// Cómo quedaría el margen a ese precio. NO escribe nada. El cálculo lo hace el backend a
// propósito: si la pantalla reimplementara la cascada, el número que ves mientras tipeás y el
// que valida el cambio podrían separarse sin que nada avise (el defecto que ya tenemos entre
// el storefront y el backend). `price` viaja como NÚMERO, nunca como texto.
export function previewMlPrice(externalItemId, price) {
    return request('ml/priceCheck/preview', {
        method: 'POST',
        body: JSON.stringify({ externalItemId, price }),
    });
}

// ⚠️ ESCRIBE EN MERCADOLIBRE: cambia el precio de la publicación de verdad.
export function setMlPrice(externalItemId, price) {
    return request('ml/priceCheck/price', {
        method: 'POST',
        body: JSON.stringify({ externalItemId, price }),
    });
}
